// The Referrals card on a person's page (docs/referrals-spec.md 4.8), owner and admin only: their code, a tick
// per product with an offer (the same write as Who can refer), their own introductions (up to 10, newest
// first, each opening on Referrals, with what it waits for when Adam hasn't decided it yet) and who introduced them,
// when someone did. Its reads are its own, so a read that fails says so here and leaves the rest of the page as it
// is; after a tick, onChanged reads the page again, so History shows the tick. On a phone it's folded to its heading,
// with the count of their introductions (people/Cards.jsx PhoneFold, the critique's admin 17).
import { useMemo, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { Card, Checkbox, Loading, Problem, StatusPill, adminPath, useAdmin, useLoad } from '../ui/index.js'
import { sentenceName, shortName } from '../people/words.js'
import { PhoneFold } from '../people/Cards.jsx'
import { REF, STATUS_WORDS, dayMonthYear, firstName, flagWords, gbp, memberSees, refusal } from './words.js'
import { loadPersonReferrals, productsWithOffers } from './data.js'
import '../../styles/admin-referrals.css'

const TONE = { applied: 'paused', joined: 'paused', qualified: 'attention', paid: 'active', not_proceeding: 'ended' }

export function PersonReferrals({ person, onChanged }) {
  const { api, products, setFlash } = useAdmin()
  const load = useLoad(() => loadPersonReferrals(api, person.id), [api, person.id])
  const [busy, setBusy] = useState(null)
  const d = load.data
  const withOffers = useMemo(() => productsWithOffers(products, d?.offers || []), [products, d])
  const productBy = useMemo(() => new Map(products.map(p => [p.id, p])), [products])

  async function tick(p, ticked) {
    if (busy) return
    setBusy(p.id)
    // the tick shows at once; the read after the write shows what the database holds (a refusal puts it back)
    load.setData(d => (d ? { ...d, ticks: ticked ? [...d.ticks, { person_id: person.id, product_id: p.id }] : d.ticks.filter(t => t.product_id !== p.id) } : d))
    try {
      await api.set_referral_tick({ person: person.id, product: p.id, ticked })
      const first = firstName(person)
      setFlash({ text: ticked ? REF.ticked(first, sentenceName(p)) : REF.unticked(first, sentenceName(p)) })
      load.reload()
      onChanged?.()
    } catch (e) {
      setFlash({ text: refusal(e), tone: 'problem' })
      load.reload()
    } finally { setBusy(null) }
  }

  let body
  let count = null
  if (load.status === 'loading' && !d) body = <Loading />
  else if (!d) body = <Problem error={load.error} onRetry={load.reload} />
  else {
    const code = d.codes.find(c => c.is_primary) || null
    const ticked = new Set(d.ticks.map(t => t.product_id))
    const theirs = d.lines.filter(l => l.referrer_person_id === person.id).slice(0, 10)
    count = d.lines.filter(l => l.referrer_person_id === person.id).length
    const by = d.lines.filter(l => l.referred_person_id === person.id && l.referrer_person_id !== person.id && memberSees(l) && l.status !== 'not_proceeding')
    const productWord = l => (l.product_id ? shortName(productBy.get(l.product_id) || { name: l.product_name }) : REF.noProduct)
    body = (
      <>
        <p className="rf-pc-code">
          {code
            ? <>{REF.card.code}: <code className="rf-code">{code.code}</code></>
            : REF.card.noCode}
        </p>
        <div className="rf-pc-part">
          <h3 className="rf-h3">{REF.card.canRefer}</h3>
          {withOffers.length
            ? (
              <div className="rf-pc-ticks">
                {withOffers.map(p => (
                  <Checkbox key={p.id} checked={ticked.has(p.id)} disabled={Boolean(busy)} onChange={v => tick(p, v)}
                    aria-label={REF.canRefer(person.full_name, sentenceName(p))}>{p.name}</Checkbox>
                ))}
              </div>
            )
            : <p className="rf-muted">{REF.card.noOffers}</p>}
        </div>
        <div className="rf-pc-part">
          <h3 className="rf-h3">{REF.card.theirs}</h3>
          {theirs.length
            ? (
              <ul className="rf-pc-lines">
                {theirs.map(l => (
                  <li key={l.id} className="rf-pc-line">
                    <span className="rf-pc-line-text">{l.referred_name || REF.someone}, {productWord(l)}</span>
                    <StatusPill tone={TONE[l.status] || 'paused'}>{STATUS_WORDS[l.status] || l.status}</StatusPill>
                    {flagWords(l) && <span className="rf-flag">{flagWords(l)}</span>}
                    {l.payout_amount != null && <span className="rf-pc-amount">{gbp(l.payout_amount)}</span>}
                    <Link to={adminPath.referralLine(l.id)} className="rf-pc-open">{REF.card.open}</Link>
                  </li>
                ))}
              </ul>
            )
            : <p className="rf-muted">{REF.card.none}</p>}
        </div>
        {by.map(l => (
          <p key={l.id} className="rf-pc-by">{REF.card.introducedBy(l.referrer_full_name, productWord(l), dayMonthYear(l.introduced_at))}</p>
        ))}
      </>
    )
  }
  return (
    <PhoneFold title={REF.card.title} count={count}>
      <Card title={REF.card.title} id="rf-person-card" className="rf-person-card">{body}</Card>
    </PhoneFold>
  )
}
